import {
  executeStatefulAPICall,
  initiateApiCall,
  executeStatefulGraphQLQuery,
} from '@tcp/core/src/services/handler';
import endpoints from '@tcp/core/src/services/endpoints';
import CacheConfiguration from '@tcp/core/src/services/handler/graphQL/clients/retryConfiguration';
import getBIAProductsAPI from '@tcp/core/src/queries/getBIAProducts.query.graphql';
import {
  BIA_DEFAULT_CACHE_KEY,
  BIA_SUFFIX,
  LOAD_NEXT_PAGE_PRODUCTS,
  PROD_PART_NO,
  TCP_PREV_STYLE,
  OR_SEPARATOR,
  BIA_TOTAL,
} from '@tcp/core/src/components/features/account/BuyItAgain/container/BuyItAgain.constants';
import processHelpers from '@tcp/core/src/services/abstractors/productListing/processHelpers';
import { extractExtraImages } from '@tcp/core/src/services/abstractors/productListing/productListing.utils';
import {
  checkIfBundleProduct,
  getColorsMap,
  isBopisProduct,
  extractAttributeValue,
} from '@tcp/core/src/services/abstractors/productListing/productParser';
import { getImgPath } from '@tcp/core/src/components/features/browse/ProductListingPage/util/utility';
import logger from '@tcp/core/src/utils/loggerInstance';
import { setLocalStorage, getLocalStorage } from '@tcp/core/src/utils/localStorageManagement';
import {
  getLocalStorageExpireTime,
  isCanada,
  getDateObject,
  updateFilterFacet,
} from '@tcp/core/src/utils';

const RES_NULL_ERROR = 'res body is null';

export const isBopisEligibleProduct = (isUSStore, product) => {
  return isBopisProduct(isUSStore, product) && !processHelpers.isGiftCard(product);
};
export const getDataFromStorage = async (cacheKey, currentTime) => {
  let cachedData;
  if (window && window.localStorage) {
    const cachedDataFromStorage = JSON.parse(getLocalStorage(cacheKey));
    if (cachedDataFromStorage?.expiresAt > currentTime) {
      cachedData = cachedDataFromStorage;
    }
  }
  return cachedData;
};

export const getMobileLocalStorageExpireTime = (cacheTTL) => {
  return getDateObject(
    getDateObject()?.setHours(getDateObject()?.getHours() + cacheTTL)
  )?.getTime();
};

export const setDataInStorage = async (cacheKey, data) => {
  setLocalStorage({ key: cacheKey, value: JSON.stringify(data) });
};
export const getReview = (product) => {
  return (
    (product.TCPBazaarVoiceReviewCount && parseInt(product.TCPBazaarVoiceReviewCount, 10)) || 0
  );
};

export const getSortedProducts = (order, products) => {
  if (!order || !products?.length) return [];
  const map = {};
  const productsArray = products.slice();
  const comparator = (a, b) => a.index - b.index;
  if (Array.isArray(order)) {
    for (let i = 0; i < order.length; i += 1) {
      map[order[i]] = i;
    }

    productsArray.forEach((product) => {
      if (product.uniqueId in map) {
        // eslint-disable-next-line no-param-reassign
        product.primaryBrand = !isCanada()
          ? extractAttributeValue(product, 'TCPPrimaryBrandUSStore')
          : extractAttributeValue(product, 'TCPPrimaryBrandCanadaStore');
        // eslint-disable-next-line no-param-reassign
        product.index = map[product.uniqueId];
      }
    });

    return productsArray.sort(comparator);
  }
  return [];
};

export const getProductInfo = (product, isBundleProduct) => {
  return {
    generalProductId: product.prodpartno || product.generalProductId,
    name: product.product_name,
    pdpUrl: `${isBundleProduct ? '/b' : '/p'}/${product.seo_token || product.uniqueId}`,
    uniqueId: product.uniqueId,
    shortDescription: product.product_short_description,
    longDescription: product.product_short_description,
    isGiftCard: processHelpers.isGiftCard(product),
    listPrice: processHelpers.getListPriceResponse(product),
    offerPrice: processHelpers.getOfferPriceResponse(product),
    long_product_title: product.long_product_title || '',
    bundleProduct: isBundleProduct,
    ratings: product.TCPBazaarVoiceRating,
    reviewsCount: getReview(product),
    primaryBrand: !isCanada()
      ? extractAttributeValue(product, 'TCPPrimaryBrandUSStore')
      : extractAttributeValue(product, 'TCPPrimaryBrandCanadaStore'),
  };
};

export const getPdpInfo = (product) => {
  return {
    seo_token: product.seo_token,
  };
};

export const processProductData = (products) => {
  return Array.isArray(products)
    ? products.map((product) => {
        const listOfAttributes = processHelpers.getProductAttributes();
        const defaultColor = processHelpers.getDefaultColor(product);
        const isUSStore = !isCanada();
        const { uniqueId } = product;
        const rawColors = processHelpers.getRawColors(isUSStore, product);
        const isBOPIS = isBopisEligibleProduct(isUSStore, product);
        const isBundleProduct = checkIfBundleProduct(product);
        const imageExtension = product.productimage || '';
        const imagesByColor = extractExtraImages(
          rawColors,
          product.alt_img,
          getImgPath,
          uniqueId,
          defaultColor,
          false,
          false,
          imageExtension
        );
        const colorsMap = getColorsMap({
          uniqueId,
          product,
          attributesNames: listOfAttributes,
          categoryType: {},
          excludeBadge: undefined,
          isBOPIS,
          bossDisabledFlags: false,
          defaultColor,
          getImgPath,
          isBundleProduct,
        });
        const productInfo = getProductInfo(product, isBundleProduct);
        const pdpInfo = getPdpInfo(product);
        return {
          styleWithData: pdpInfo,
          productInfo,
          uniqueId,
          miscInfo: {},
          categoryPath2: product.categoryPath2,
          TCPColor: product.TCPColor,
          categoryPath2_catMap: product.categoryPath2_catMap,
          colorsMap,
          imagesByColor,
          lastPurchasedSize: product.lastPurchasedSize,
        };
      })
    : [];
};

const getPaginatedProducts = (buyItAgainProducts, initialProductsLoaded) => {
  if (initialProductsLoaded > buyItAgainProducts.length) return [];
  if (
    buyItAgainProducts.length > initialProductsLoaded &&
    buyItAgainProducts.length >= LOAD_NEXT_PAGE_PRODUCTS
  ) {
    const calculatedLastIndex = initialProductsLoaded + LOAD_NEXT_PAGE_PRODUCTS;
    const endIndex =
      calculatedLastIndex > buyItAgainProducts ? buyItAgainProducts : calculatedLastIndex;
    return buyItAgainProducts.slice(initialProductsLoaded, endIndex);
  }
  return buyItAgainProducts.slice();
};

export const getBuyItAgainProductsGraphApi = (
  { cacheTTL, sortingOrder, dateOnlySorting, categoryCode, dateSortingOrder, fetchAllRecords },
  skipCache = false
) => {
  const { defaultTTL } = CacheConfiguration.GET_BIA_PRODUCTS;
  let cacheKey;
  if (categoryCode) {
    cacheKey = `${categoryCode}-${BIA_SUFFIX}`;
  } else if (fetchAllRecords) {
    cacheKey = BIA_DEFAULT_CACHE_KEY;
  } else {
    cacheKey = BIA_SUFFIX;
  }
  return executeStatefulGraphQLQuery({
    query: getBIAProductsAPI,
    type: 'query',
    header: {
      sortingOrder: sortingOrder || '',
      dateOnlySorting: dateOnlySorting || false,
      categoryCode: categoryCode || '',
      dateSortingOrder: dateSortingOrder || '',
      fetchAllRecords: fetchAllRecords || false,
    },
    cacheConfigKey: `${cacheKey}`,
    TTL: cacheTTL || defaultTTL,
    fetchPolicy: skipCache ? 'network-only' : '',
  })
    .then(async (response) => {
      if (!response.data) {
        throw new Error(RES_NULL_ERROR);
      }
      await setDataInStorage(
        BIA_TOTAL,
        response?.data?.getUserPurchaseHistory?.biaProducts?.length
      );

      return (
        response.data &&
        response.data.getUserPurchaseHistory &&
        response.data.getUserPurchaseHistory.biaProducts
      );
    })
    .catch((error) => {
      logger.error('error occurred while triggering the getBuyItAgainProductsGraphApi ', error);
    });
};

export const getBuyItAgainProductsApi = async (
  cacheTTL,
  sortingOrder,
  dateOnlySorting,
  categoryCode,
  dateSortingOrder,
  fetchAllRecords
) => {
  const { getBuyItAgainProducts } = endpoints;
  const payloadData = {
    webService: getBuyItAgainProducts,
    header: {
      sortingOrder: sortingOrder || '',
      dateOnlySorting: dateOnlySorting || false,
      categoryCode: categoryCode || '',
      dateSortingOrder: dateSortingOrder || '',
      fetchAllRecords: fetchAllRecords || false,
    },
  };
  let cacheKey;
  if (categoryCode) {
    cacheKey = `${categoryCode}-${BIA_SUFFIX}`;
  } else if (fetchAllRecords) {
    cacheKey = BIA_DEFAULT_CACHE_KEY;
  } else {
    cacheKey = BIA_SUFFIX;
  }
  const buyItAgainProductsCacheData = {};
  let res = null;
  let cachedData;
  const currentTime = Date.now();
  try {
    cachedData = await getDataFromStorage(cacheKey, currentTime);
    if (cachedData && cachedData?.data && cachedData?.data?.buyItAgainProducts !== null)
      return cachedData.data;
    res = await executeStatefulAPICall(payloadData);
    buyItAgainProductsCacheData.timestamp = currentTime;
    buyItAgainProductsCacheData.expiresAt = getLocalStorageExpireTime(cacheTTL);
    buyItAgainProductsCacheData.data = res?.body?.data;
    await setDataInStorage(cacheKey, buyItAgainProductsCacheData);
  } catch (error) {
    logger.error('error occurred while triggering the getBuyItAgainProductsApi ', error);
  }
  return res?.body?.data;
};

export const getBuyItAgainProductsBatchesGraphQl = async (
  initialProductsLoaded,
  cacheTTL,
  fetchAllRecords
) => {
  try {
    const res = await getBuyItAgainProductsGraphApi({
      cacheTTL,
      sortingOrder: null,
      dateOnlySorting: true,
      categoryCode: null,
      dateSortingOrder: null,
      fetchAllRecords,
    });
    const productIds = res?.map((item) => item.productId);
    const paginatedProductIds = getPaginatedProducts(productIds, initialProductsLoaded);
    return {
      productIdArray: paginatedProductIds,
      resp: res,
    };
  } catch (error) {
    logger.error({
      error,
      extraData: {
        component: 'Buy It Again abstractor - getBuyItAgainProductsBatchesGraphQl',
      },
    });
  }
  return null;
};

export const getBuyItAgainProductsBatches = async (
  initialProductsLoaded,
  cacheTTL,
  fetchAllRecords
) => {
  try {
    const cachedData = JSON.parse(getLocalStorage(BIA_DEFAULT_CACHE_KEY));
    if (cachedData?.expiresAt > Date.now()) {
      const {
        data: { buyItAgainProducts },
      } = cachedData;
      return getPaginatedProducts(buyItAgainProducts, initialProductsLoaded);
    }
    const res = await getBuyItAgainProductsApi(cacheTTL, null, true, null, null, fetchAllRecords);
    return getPaginatedProducts(res.buyItAgainProducts, initialProductsLoaded);
  } catch (error) {
    logger.error({
      error,
      extraData: {
        component: 'Buy It Again abstractor - getBuyItAgainProductsBatches',
      },
    });
  }
  return null;
};

export const getTotalProductsCount = async (isBiaEnhancmentEnabled) => {
  if (window && window.localStorage) {
    if (isBiaEnhancmentEnabled) {
      return JSON.parse(getLocalStorage(BIA_TOTAL));
    }
    const cachedData = JSON.parse(getLocalStorage(BIA_DEFAULT_CACHE_KEY));
    return cachedData?.data?.buyItAgainProducts?.length;
  }
  return 0;
};

export const getFilterForApi = (productIds, disableStyledSearch) => {
  if (!productIds) return null;

  const ids = productIds.map((id) =>
    disableStyledSearch
      ? `${PROD_PART_NO}:${id}`
      : `${PROD_PART_NO}:${id}${OR_SEPARATOR}${TCP_PREV_STYLE}:${id}`
  );
  return ids.join(OR_SEPARATOR);
};

export const getProductDetails = async (
  productIds,
  endPoint,
  rows,
  disableStyledSearch = false,
  isFromBIA
) => {
  if (!productIds) return null;
  let res = null;
  const filter = getFilterForApi(productIds, disableStyledSearch);
  const isVQtyFilter = updateFilterFacet();
  const updatedFilter = isVQtyFilter ? `${filter}&filter=${isVQtyFilter.filter}` : filter;
  const numberOfRows = rows || 20;
  const body = {
    rows: numberOfRows,
    variants: true,
    'variants.count': 100,
    version: 'V2',
    'facet.multiselect': true,
    selectedfacet: true,
    q: '*',
    filter: updatedFilter,
    pagetype: 'boolean',
    isFromBIA,
    fields:
      'productimage,alt_img,style_partno,giftcard,TCPProductIndUSStore,TCPWebOnlyFlagUSStore,TCPWebOnlyFlagCanadaStore,TCPFitMessageUSSstore,TCPFit,product_name,TCPColor,top_rated,imagename,productid,uniqueId,favoritedcount,TCPBazaarVoiceReviewCount,categoryPath3_catMap,categoryPath2_catMap,product_short_description,style_long_description,min_list_price,min_offer_price,TCPBazaarVoiceRating,product_long_description,seo_token,variantCount,prodpartno,variants,v_tcpfit,v_qty,v_tcpsize,style_name,v_item_catentry_id,v_listprice,v_offerprice,v_qty,variantId,auxdescription,list_of_attributes,additional_styles,TCPLoyaltyPromotionTextUSStore,TCPLoyaltyPLCCPromotionTextUSStore,v_variant,low_offer_price,high_offer_price,low_list_price,high_list_price,long_product_title,TCPCustomSalePrice,TCPCustomListPrice',
  };
  try {
    res = await initiateApiCall({ body, webService: endPoint });
    return res.body.response.products;
  } catch (error) {
    logger.error({
      error,
      extraData: {
        component: 'Buy It Again abstractor - getProductDetails',
      },
    });
    return res;
  }
};

export default getBuyItAgainProductsApi;
